/**
 * Utility functions for page config related functionality in search
 */
import SearchExperiments from 'bundles/epic/clients/search';
import { SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import type { SearchStates } from 'bundles/search-common/providers/searchTypes';

import amazonWebServices from 'images/search-common/aws.png';
import deeplearningai from 'images/search-common/deeplearningai.png';
import google from 'images/search-common/google.png';
import googleCloud from 'images/search-common/googlecloud.png';
import ibm from 'images/search-common/ibm.png';
import meta from 'images/search-common/meta.png';
import microsoft from 'images/search-common/microsoft.png';
import penn from 'images/search-common/penn.png';
import stanford from 'images/search-common/stanford.png';
import umich from 'images/search-common/umich.png';

import _t from 'i18n!nls/search-common';

/**
 * List of queries that should use the page config system for free content experience
 */
export const FREE_QUERIES = [
  'free',
  'free courses with certificate',
  'free courses',
  'free certificate courses',
  'free course with certificate',
  'free  courses with certificate',
  'free course',
  'free/',
  'free certificate',
  'free guided projects',
  'free certificate course',
  'free degree',
  'free online courses',
  'free certification',
  'free certificat courses',
  'free cources',
  'free courses with free certificate',
  'free courses with certificates',
  'free  courses',
  'free certification courses',
  'free certifications',
  '1 hour free',
  'free course less than 2 hours',
  'free projects',
  '/free',
  'free with certificate',
  'free online course',
  'free certification course',
  'free corse',
  'free online courses with certificate',
  `gratis`,
  `cursos gratis`,
];

/**
 * Determines if a search query should use the page config system for free content
 */
export const isFreeQuery = (query?: string | string[]): boolean => {
  if (!query) return false;
  let normalizedQuery = query;
  if (Array.isArray(normalizedQuery)) {
    // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    normalizedQuery = normalizedQuery.filter(Boolean)[0];
  }
  if (!normalizedQuery) return false;
  // @ts-expect-error TS2339 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  normalizedQuery = normalizedQuery.trim().toLowerCase();
  // @ts-expect-error TS2769 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  return FREE_QUERIES.includes(normalizedQuery);
};

export const isInFreeQueryExperimentImpression = (): boolean => {
  return SearchExperiments.get('freeQuery') === true;
};

export const isInFreeQueryExperiment = (): boolean => {
  return SearchExperiments.preview('freeQuery') === true;
};

/**
 * Determines if a search should use the page config system
 * based on query content and experiment status, impress only if they are part of FREE_QUERIES
 */
export const shouldShowFreeQueryPageConfig = (query?: string): boolean => {
  return isFreeQuery(query) && isInFreeQueryExperimentImpression();
};

export const shouldShowFreeQueryPageConfigPreview = (query?: string): boolean => {
  return isFreeQuery(query) && isInFreeQueryExperiment();
};

type FreeQueryCounts = {
  freeCount: number;
  freeTrialCount: number;
};

export const freeQueriesValuePropCounts = (): FreeQueryCounts => {
  return { freeCount: 1112, freeTrialCount: 7815 };
};

export const MEDIUM_INTENT_PARTNER_VALUE_COUNT = 446;

export const MEDIUM_INTENT_PLACEHOLDER_ADDITIONAL_CONTENT = [
  { value: 0, category: 'credentials' },
  { value: 0, category: 'online degrees' },
  { value: 0, category: 'courses' },
];

export const PARTNERS_MEDIUM_INTENT_PLACEHOLDER_ADDITIONAL_CONTENT = [
  { value: MEDIUM_INTENT_PARTNER_VALUE_COUNT, category: 'partners' },
  { value: 0, category: 'courses' },
];

export const getFacetsSearchConfig = (query?: string): SearchStates => ({
  facets: {
    id: SEARCH_MAIN_INDEX_ID,
    entityType: 'PRODUCTS' as const,
    limit: 0,
    query: query || '',
    facets: ['productTypeDescription'],
    maxValuesPerFacet: 10,
  },
});

export const getMediumIntentPartnerData = () => [
  {
    relationSlug: 'google',
    imageAltText: _t('Google'),
    partnerName: 'Google',
    image: { url: google },
  },
  {
    relationSlug: 'ibm',
    imageAltText: _t('IBM'),
    partnerName: 'IBM',
    image: { url: ibm },
  },
  {
    relationSlug: 'deeplearning.ai',
    imageAltText: _t('DeepLearning.AI'),
    partnerName: 'DeepLearning.AI',
    image: { url: deeplearningai },
  },
  {
    relationSlug: 'microsoft',
    imageAltText: _t('Microsoft'),
    partnerName: 'Microsoft',
    image: { url: microsoft },
  },
  {
    relationSlug: 'stanford university',
    imageAltText: _t('Stanford University'),
    partnerName: 'Stanford University',
    image: { url: stanford },
  },
  {
    relationSlug: 'meta',
    imageAltText: _t('Meta'),
    partnerName: 'Meta',
    image: { url: meta },
  },

  {
    relationSlug: 'university of pennsylvania',
    imageAltText: _t('University of Pennsylvania'),
    partnerName: 'University of Pennsylvania',
    image: { url: penn },
  },
  {
    relationSlug: 'google cloud',
    imageAltText: _t('Google Cloud'),
    partnerName: 'Google Cloud',
    image: { url: googleCloud },
  },
  {
    relationSlug: 'university of michigan',
    imageAltText: _t('University of Michigan'),
    partnerName: 'University of Michigan',
    image: { url: umich },
  },
  {
    relationSlug: 'amazon web services',
    imageAltText: _t('Amazon Web Services'),
    partnerName: 'Amazon Web Services',
    image: { url: amazonWebServices },
  },
];
